import React, { ReactElement } from "react"
import { defineMessage } from "react-intl"
import { Stack, useTheme } from "@carrotfertility/carotene-core"
import { CarrotPlansPaths } from "#/utils/Paths"
import { useStartRouting } from "../../carrot-plans/shared/useQueryStartRouting"
import { useHistory } from "react-router-dom"
import { useCurrentUser } from "../../context/user/UserContext"
import useCarrotPlanEmployeeSettings from "../../carrot-plans/hooks/useCarrotPlanEmployeeSettings"
import { ProgramType } from "#/lib/carrot-api/types/Benefit"
import {
  ContentfulBenefitHighlightsContent,
  CalibratedEngagementCta
} from "#/components/home/benefits-modules/BenefitHighlightInnerContent"
import { useGetCarrotPlanRequestStatus } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"
import { CarrotPlanTile } from "#/components/home/carrot-plan/CarrotPlanTile"
import { RoutingFlowType } from "#/components/carrot-plans/types/routingFlowType"
import { useCalibratedEngagementEnabled } from "#/hooks/calibratedEngagement/useCalibratedEngagement"
import useGetBenefit from "#/components/dynamic-content/hooks/useGetBenefit"

type UseBenefitHighlightComponentSetUp = {
  isDoneForNow: boolean
  startRouting: () => Promise<void>
  shouldSeeEngageHero: boolean
  areAnyLoading: boolean
  showContinueCopy: boolean
  carrotPlanIsOnTheWay: boolean
  programTypeAllowsBenefitHighlight: boolean
  calibratedEngagementEnabled: boolean
}

function useBenefitHighlightsComponentSetUp(): UseBenefitHighlightComponentSetUp {
  const benefitQuery = useGetBenefit()
  const programType = benefitQuery.data?.program.type
  const programTypeAllowsBenefitHighlight = programType === ProgramType.PRO || programType === ProgramType.CORE
  const {
    data: carrotPlanEmployeeSettings,
    isLoading: isLoadingEmployeeSettings,
    isError: isErrorEmployeeSettings,
    isDoneForNow
  } = useCarrotPlanEmployeeSettings()
  const { carrotPlansRoutingStarted } = useCurrentUser()
  const { startRouting } = useStartRouting(RoutingFlowType.CASE_RATE_RENEWAL)
  const cpRequestStatusQuery = useGetCarrotPlanRequestStatus()
  const calibratedEngagementEnabled = useCalibratedEngagementEnabled()
  const shouldSeeEngageHero = !programTypeAllowsBenefitHighlight && programType === ProgramType.WTW_ENGAGE
  const areAnyLoading =
    benefitQuery.isLoading ||
    isLoadingEmployeeSettings ||
    cpRequestStatusQuery.isLoading ||
    calibratedEngagementEnabled.isLoading
  const areAnyInError = isErrorEmployeeSettings || cpRequestStatusQuery.isError || benefitQuery.isError
  const showContinueCopy =
    carrotPlansRoutingStarted && !areAnyInError && Boolean(carrotPlanEmployeeSettings?.routingInfoId)

  return {
    isDoneForNow,
    startRouting,
    shouldSeeEngageHero,
    areAnyLoading,
    showContinueCopy,
    carrotPlanIsOnTheWay: cpRequestStatusQuery.data?.carrotPlanIsOnTheWay,
    programTypeAllowsBenefitHighlight,
    calibratedEngagementEnabled: calibratedEngagementEnabled.data
  }
}

function getTileText(showContinueCopy: boolean) {
  const buttonText = showContinueCopy
    ? defineMessage({ defaultMessage: "Continue" })
    : defineMessage({ defaultMessage: "Get your Carrot Plan" })

  const messageText = showContinueCopy
    ? defineMessage({
        defaultMessage:
          "Continue telling us about your journey so we can match you with a Carrot Companion who will create your Carrot Plan."
      })
    : defineMessage({
        defaultMessage:
          "When you're ready to unlock full access to your benefit, share more about your journey and we'll match you with a dedicated Carrot Companion who will craft a free, personalized plan to support you."
      })

  return {
    buttonText,
    messageText
  }
}

function BenefitHighlights(): ReactElement {
  const theme = useTheme()
  const history = useHistory()
  const {
    areAnyLoading,
    showContinueCopy,
    shouldSeeEngageHero,
    carrotPlanIsOnTheWay,
    isDoneForNow,
    startRouting,
    programTypeAllowsBenefitHighlight,
    calibratedEngagementEnabled
  } = useBenefitHighlightsComponentSetUp()
  const { buttonText, messageText } = getTileText(showContinueCopy)
  const showCarrotPlanTile = !carrotPlanIsOnTheWay && !calibratedEngagementEnabled
  const showCalibratedEngagementCta = calibratedEngagementEnabled && !carrotPlanIsOnTheWay

  if (areAnyLoading || (shouldSeeEngageHero && carrotPlanIsOnTheWay)) {
    return null
  }

  async function handleClick(): Promise<Promise<void> | void> {
    if (isDoneForNow) {
      await startRouting()
    } else {
      history.push(CarrotPlansPaths.ENROLLMENT)
    }
  }

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
      {showCarrotPlanTile && (
        <CarrotPlanTile messageText={messageText} buttonText={buttonText} handleClick={handleClick} />
      )}
      {programTypeAllowsBenefitHighlight && (
        <>
          <ContentfulBenefitHighlightsContent />
          {showCalibratedEngagementCta && <CalibratedEngagementCta />}
        </>
      )}
    </Stack>
  )
}

export { BenefitHighlights }
